import { useMutation, useQuery } from '@apollo/client';
import {
  CommandBarButton,
  DefaultButton,
  ITooltipHostStyles,
  IconButton,
  Shimmer,
  ShimmerElementType,
  Stack,
  Text,
  TooltipHost,
} from '@fluentui/react';
import { ToggleButton } from 'common/components/Buttons/ToggleButton';
import { Chip } from 'common/components/Chip';
import { NoDataView } from 'common/components/DataPlaceholders';
import { EntityTagInput } from 'common/types/globalTypes';
import { dateFormat } from 'common/utils/dateFormats';
import React, { useCallback, useState } from 'react';
import { useStyles } from 'common/components/ActionMenu/Tags/index.styles';
import {
  EntityTagApprovalCreate,
  EntityTagApprovalCreateVariables,
} from './__generated__/EntityTagApprovalCreate';
import {
  ApprovalAvailableTags,
  ApprovalAvailableTagsVariables,
} from './__generated__/ApprovalAvailableTags';
import {
  ListApprovalTags,
  ListApprovalTagsVariables,
} from './__generated__/ListApprovalTags';
import {
  EntityTagDelete,
  EntityTagDeleteVariables,
} from 'common/graphql/__generated__/EntityTagDelete';

import LIST_APPROVAL_TAGS from './ListApprovalTags.graphql';
import APPROVAL_AVAILABLE_TAGS from './ApprovalAvailableTags.graphql';
import ENTITY_TAG_APPROVAL_CREATE from './EntityTagApprovalCreate.graphql';
import ENTITY_TAG_DELETE from 'common/graphql/EntityTagDelete.graphql';
interface TagsProps {
  approvalId: string;
}
interface TagsType {
  id: string;
  _rowTimestamp: string;
}

export const Tags: React.FC<TagsProps> = ({ approvalId }) => {
  const styles = useStyles();
  const [toggleView, setToggleView] = useState(false);
  const [selectedTags, setSelectedTags] = React.useState<string[]>([]);

  const [addNewTag, { loading: loadingAddNewTag }] = useMutation<
    EntityTagApprovalCreate,
    EntityTagApprovalCreateVariables
  >(ENTITY_TAG_APPROVAL_CREATE);

  const [deleteTag, { loading: loadingRemoveTag }] = useMutation<
    EntityTagDelete,
    EntityTagDeleteVariables
  >(ENTITY_TAG_DELETE);

  const {
    data: tagsData,
    refetch: tagsListRefetch,
    loading: tagsLoading,
  } = useQuery<ApprovalAvailableTags, ApprovalAvailableTagsVariables>(
    APPROVAL_AVAILABLE_TAGS,
    {
      variables: {
        id: approvalId!,
      },
      skip: !approvalId,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
    }
  );

  const getAvailableTags = () => {
    const variables: ApprovalAvailableTagsVariables = {
      id: approvalId!,
    };
    tagsListRefetch(variables);
  };

  const getAvailableTagsMemo = useCallback(getAvailableTags, []);

  const {
    data: entityTagsData,
    refetch,
    loading: entityTagsLoading,
  } = useQuery<ListApprovalTags, ListApprovalTagsVariables>(
    LIST_APPROVAL_TAGS,
    {
      variables: {
        id: approvalId!,
      },
      skip: !approvalId,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
    }
  );

  const getEntityTags = () => {
    const variables: ListApprovalTagsVariables = {
      id: approvalId!,
    };
    refetch(variables);
  };

  const getEntityTagsMemo = useCallback(getEntityTags, []);

  const removeTag = async (tag: TagsType) => {
    await deleteTag({
      variables: {
        entityTagDeleteInput: {
          entityDelete: [
            {
              id: tag!.id,
              rowTimestamp: tag!._rowTimestamp!,
            },
          ],
        },
      },
    });
    setSelectedTags([]);
    if (!loadingRemoveTag) {
      getAvailableTagsMemo();
      getEntityTagsMemo();
    }
  };

  const AddTag = async () => {
    const tagsInputData = selectedTags.map((tag: string) => ({
      tagId: tag,
      entityId: approvalId,
    }));
    await addNewTag({
      variables: {
        input: {
          entityTag: tagsInputData?.map(
            ({ ...tags }) => tags as EntityTagInput
          ),
        },
      },
    });
    setSelectedTags([]);
    if (!loadingAddNewTag) {
      getAvailableTagsMemo();
      getEntityTagsMemo();
    }
  };
  const disableAddTags =
    selectedTags.length === 0 ||
    loadingAddNewTag ||
    !tagsData?.approvalAvailableTags?.nodes?.length;
  const calloutProps = { gapSpace: 0 };
  const hostStyles: Partial<ITooltipHostStyles> = {
    root: { display: 'inline-block' },
  };

  const isSelected = (tagId: string) =>
    selectedTags.some((item) => item === tagId);

  const onTagPressed = (tagId: string) => {
    let levelsSelected = [...selectedTags];
    let position = levelsSelected.findIndex((item) => item === tagId);
    if (position === -1) {
      levelsSelected.push(tagId);
    } else levelsSelected.splice(position, 1);
    setSelectedTags(levelsSelected);
  };

  return (
    <Stack
      tokens={{ childrenGap: 15, padding: '0px 25px' }}
      className={styles.tagsContainer}
    >
      {toggleView ? (
        <Stack tokens={{ childrenGap: 30 }} className={styles.tagsContent}>
          <Stack horizontal horizontalAlign="space-between">
            <Stack horizontal tokens={{ childrenGap: 10 }}>
              {tagsData?.approvalAvailableTags?.nodes.length === 0 &&
                !tagsLoading && (
                  <Stack.Item className={styles.emptyTagsView}>
                    <Text>No available tags to add!</Text>
                  </Stack.Item>
                )}
              <Stack
                wrap
                horizontal
                tokens={{ childrenGap: 10 }}
                className={styles.customWidth}
              >
                {tagsData?.approvalAvailableTags?.nodes &&
                  tagsData.approvalAvailableTags?.nodes.map((tag) => {
                    return (
                      <Stack key={tag.id}>
                        <ToggleButton
                          onClick={() => onTagPressed(tag.id)}
                          toggled={isSelected(tag.id)}
                          key={tag.id}
                          text={tag.badgeName}
                          iconName={isSelected(tag.id) ? 'Accept' : 'Add'}
                          color={
                            isSelected(tag.id) && tag.badgeColor
                              ? tag.badgeColor
                              : undefined
                          }
                        />
                      </Stack>
                    );
                  })}
              </Stack>
            </Stack>
            <IconButton
              onClick={() => setToggleView(false)}
              iconProps={{ iconName: 'Cancel' }}
              className={styles.closeButton}
              ariaLabel="Close"
            />
          </Stack>
          <Stack tokens={{ childrenGap: 20 }} horizontal>
            <CommandBarButton
              className={styles.commandBarButton}
              iconProps={{ iconName: 'Accept' }}
              disabled={disableAddTags}
              onClick={() => {
                AddTag();
              }}
              text={'Add tags'}
            />
            <CommandBarButton
              className={styles.commandBarButton}
              iconProps={{ iconName: 'Cancel' }}
              disabled={selectedTags.length === 0}
              onClick={() => {
                setSelectedTags([]);
              }}
              text="Cancel"
            />
          </Stack>
        </Stack>
      ) : (
        <Stack horizontal horizontalAlign="end">
          <DefaultButton
            onClick={() => setToggleView(true)}
            iconProps={{ iconName: 'Add' }}
            text={'Add Tags'}
          />
        </Stack>
      )}
      <>
        {entityTagsLoading && (
          <Shimmer
            shimmerElements={[
              { type: ShimmerElementType.line, width: 100, height: 40 },
            ]}
          />
        )}
        {!entityTagsLoading && (
          <Stack
            wrap
            horizontal
            tokens={{ childrenGap: 10 }}
            className={styles.customWidth}
          >
            {entityTagsData?.userApproval?.entityTagsByEntityId.nodes.map(
              (entityTag) => {
                const createdDate = dateFormat(entityTag._createdDate!);
                const createdByUser = entityTag.createdByUser;
                const tooltipContent = `${createdByUser} / ${createdDate}`;
                return (
                  <>
                    <TooltipHost
                      content={tooltipContent}
                      id="tooltipId"
                      calloutProps={calloutProps}
                      styles={hostStyles}
                      key={entityTag.id}
                    >
                      <Chip
                        disabled={loadingRemoveTag || !entityTag._isDeletable}
                        text={entityTag._tagBadgeName!}
                        onClick={() => {
                          removeTag({
                            id: entityTag.id || '',
                            _rowTimestamp: entityTag._rowTimestamp || '',
                          });
                        }}
                        color={entityTag._tagBadgeColor!}
                      />
                    </TooltipHost>
                  </>
                );
              }
            )}
          </Stack>
        )}
        <NoDataView
          title={'No tags available!'}
          show={
            entityTagsData?.userApproval?.entityTagsByEntityId.nodes.length ===
              0 && !entityTagsLoading
          }
        />
      </>
    </Stack>
  );
};
